import { useState } from 'react';
import { TOTAL_STEPS } from './types';
import { useSurvey } from '../../../../contexts/SurveyContext';
import {
  buildPayload,
  submitToGas,
  normalizePhone,
  FORM_FIELDS,
  pushDataLayerEvent,
} from '../../../../utils/tracking';
import StepQualification from './StepQualification';
import StepTiming from './StepTiming';
import StepWorkStyle from './StepWorkStyle';
import StepAddressName from './StepAddressName';
import StepPhone from './StepPhone';
import StepComplete from './StepComplete';

interface Props {
  isAffiliate?: boolean;
}

const STEP_LABELS = ['保有資格', '転職希望時期', '働き方', '最寄り駅・氏名・生年月日', '連絡先'];

// ============================================================
// 送信後のLINE友だち追加遷移（2026-08-10 追加）
// ------------------------------------------------------------
// 遷移先は @nijikango（エルメ運用のLINE公式）。
// ⚠️ 既存のLINE誘導ボタン（StepComplete）は
//    https://liff.line.me/2008134586-4lPmuiqP → line.me/R/ti/p/@266jzhpa（2号機）で、
//    こことは着地アカウントが異なる。混同しないこと。
// 流入分類は「通常LP」= utm_campaign=lp_normal。
// ============================================================
const LINE_FRIEND_ADD_URL = 'https://line.me/R/ti/p/@nijikango?utm_campaign=lp_normal';

/** 完了画面を見せてからLINEへ飛ばすまでの待ち時間（lp02と同じ1.5秒） */
const LINE_REDIRECT_DELAY_MS = 1500;
/** gtagがブロックされていてもここで必ず解決させる */
const GA4_CALLBACK_TIMEOUT_MS = 2000;

const isMobileDevice = (): boolean =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

export default function SurveyForm({ isAffiliate = false }: Props) {
  const {
    step, setStep,
    formData, onChange,
    submitting, setSubmitting,
    setIsCompleted,
  } = useSurvey();

  const [submitError, setSubmitError] = useState('');

  const goNext = () => setStep(step + 1);
  const goBack = () => setStep(step - 1);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    // ステップ5の送信ボタンクリックをGA4に送信
    pushDataLayerEvent('nijikango_click_step5');

    try {
      const personalData = {
        qualification: formData.qualifications.join(', '),
        birth_year: formData.birthYear,
        birth_month: formData.birthMonth,
        birth_day: formData.birthDay,
        name: `${formData.lastName.trim()} ${formData.firstName.trim()}`,
        phone: normalizePhone(formData.phone).value,
        email: formData.email.trim(),
        // lp02同様、最寄り駅を postal_code に載せて送信（GAS側で最寄り駅列に集約）
        postal_code: formData.nearestStation.trim(),
        prefecture: '',
        work_style: formData.workStyle,
        transfer_timing: formData.timing,
      };

      const payload = buildPayload(personalData, FORM_FIELDS);
      await submitToGas(payload);

      // 送信成功: コンバージョンイベント（個人情報はGA4に渡さず conversion_id のみ）
      pushDataLayerEvent('generate_lead', {
        conversion_id: payload.conversion_id as string,
      });
      // GTMトリガー「GA4- form_submission_success」用
      pushDataLayerEvent('form_submission_success');

      setStep(6);
      setIsCompleted(true);
      setSubmitting(false);

      // 送信成功したときだけLINEへ遷移する。
      // 先に完了画面を出す→CVイベントの発火猶予を待つ→遷移、の順にしないと
      // 遷移でページが破棄されてGA4のCVを取りこぼす（lp02で実証済みの手順）。
      // PCではLINEアプリが開けないため遷移せず、完了画面のQR/ボタンに委ねる。
      if (isMobileDevice()) {
        await new Promise((r) => setTimeout(r, Math.max(LINE_REDIRECT_DELAY_MS, GA4_CALLBACK_TIMEOUT_MS)));
        window.location.href = LINE_FRIEND_ADD_URL;
      }
    } catch {
      // 送信失敗: エラーメッセージ表示・再送信可能
      setSubmitError('送信に失敗しました。通信状況をご確認の上、もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  const isComplete = step === 6;

  return (
    <div
      style={{
        maxWidth: '600px',
        width: '100%',
        margin: '0 auto',
        border: '1px solid #ff5c73',
        borderRadius: '10px',
        background: 'white',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}
    >
      {/* Form Header */}
      <div style={{ background: '#ff5c73', color: 'white', padding: '14px 16px 14px 16px', position: 'relative', minHeight: '72px', display: 'flex', alignItems: 'center' }}>
        <div
          style={{
            position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '12px',
            width: '60px', height: '60px', borderRadius: '50%', background: 'white',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '11px', color: '#ff5c73', fontWeight: 700 }}>カンタン</span>
          <span style={{ fontSize: '22px', color: '#ff5c73', fontWeight: 900, lineHeight: 1 }}>30秒</span>
        </div>
        <p style={{ fontWeight: 700, fontSize: '14px', lineHeight: 1.6, paddingRight: '72px' }}>
          簡単な質問に答えるだけで
          <br />
          あなたにぴったりの求人をご紹介!
        </p>
      </div>

      {/* Step Dots */}
      {!isComplete && (
        <div
          style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px',
            padding: '10px 16px', background: '#fff8fa', borderBottom: '1px solid #ffe0e8',
          }}
        >
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
            <div
              key={s}
              title={STEP_LABELS[s - 1]}
              style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: s <= step ? '#ff5c73' : '#ddd',
                transition: 'background 0.3s ease',
              }}
            />
          ))}
          <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '6px' }}>{Math.min(step, TOTAL_STEPS)}/{TOTAL_STEPS}</span>
        </div>
      )}

      {/* Step Content */}
      <div style={{ padding: '20px 16px' }}>
        {step === 1 && (
          <StepQualification
            formData={formData}
            onChange={onChange}
            onNext={() => { pushDataLayerEvent('nijikango_click_step1'); goNext(); }}
          />
        )}
        {step === 2 && (
          <StepTiming
            formData={formData}
            onChange={onChange}
            onNext={() => { pushDataLayerEvent('nijikango_click_step2'); goNext(); }}
            onBack={goBack}
          />
        )}
        {step === 3 && (
          <StepWorkStyle
            formData={formData}
            onChange={onChange}
            onNext={() => { pushDataLayerEvent('nijikango_click_step3'); goNext(); }}
            onBack={goBack}
          />
        )}
        {step === 4 && (
          <StepAddressName
            formData={formData}
            onChange={onChange}
            onNext={() => { pushDataLayerEvent('nijikango_click_step4'); goNext(); }}
            onBack={goBack}
          />
        )}
        {step === 5 && (
          <>
            <StepPhone
              formData={formData}
              onChange={onChange}
              onBack={goBack}
              onSubmit={handleSubmit}
              submitting={submitting}
            />
            {/* 送信失敗時のエラーメッセージ */}
            {submitError && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px 14px',
                  background: '#fff5f5',
                  border: '1.5px solid #ff5c73',
                  borderRadius: '8px',
                  color: '#cc2233',
                  fontSize: '13px',
                  lineHeight: 1.6,
                  textAlign: 'center',
                }}
              >
                <i className="ri-error-warning-line" style={{ marginRight: '6px' }} />
                {submitError}
              </div>
            )}
          </>
        )}
        {step === 6 && <StepComplete isAffiliate={isAffiliate} />}
      </div>
    </div>
  );
}